import { cva } from "class-variance-authority";
import { TABLE_RADIUS } from "./constants";

/**
 * One look for every table in the app (Figma 6222:4023): a rounded card with a
 * tinted header strip. A table never changes shape depending on where it sits —
 * a section that would otherwise wrap it in a second box drops its own chrome
 * instead, so the title row sits above the card, never around it.
 *
 * The only knob is `density`: how much air each row gets.
 */
export const tableContainer = `overflow-hidden ${TABLE_RADIUS} border border-gray-200/60 bg-white dark:border-white/[0.05] dark:bg-gray-900`;

export const tableHeaderRow =
	"border-b border-gray-100 bg-gray-50 dark:border-white/[0.06] dark:bg-white/[0.03]";

/**
 * A sticky header scrolls over the rows, so its cells need a fill of their own —
 * the header's translucent tint would let rows show through. Opaque `gray-800`
 * is the closest solid step to that tint over the container's `gray-900`.
 */
export const tableStickyHeaderCell =
	"sticky top-0 z-20 bg-gray-50 dark:bg-gray-800";

export const tableHeaderCell =
	"text-start text-theme-xs font-medium text-gray-500 dark:text-gray-400";

export const tableBody = "divide-y divide-gray-100 dark:divide-white/[0.05]";

export const tableRow = "transition-colors";

export const tableRowInteractive =
	"cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03]";

export const tableCell =
	"text-start text-theme-sm text-gray-500 dark:text-gray-400";

/**
 * Typography inside cells. Column definitions compose these instead of writing
 * their own greys and weights — that's what made the same kind of value look
 * different from one table to the next.
 */

/** The value a row is about: name, amount, date. */
export const tableCellPrimary =
	"text-theme-sm font-medium text-gray-800 dark:text-white/90";

/** Sub-line under a primary value (category, periodicity, end date). */
export const tableCellSecondary =
	"text-theme-xs text-gray-500 dark:text-gray-400";

/** Supporting column that shouldn't compete with the primary value. */
export const tableCellMuted = "text-theme-sm text-gray-600 dark:text-gray-400";

/** Add to any numeric cell so digits line up across rows. */
export const tableCellNumeric = "tabular-nums whitespace-nowrap";

/** A gain: savings, a payment going down. */
export const tableCellPositive =
	"text-theme-sm font-semibold text-success-600 dark:text-success-400";

/** A loss: a payment going up, negative cash flow. */
export const tableCellNegative =
	"text-theme-sm font-semibold text-error-600 dark:text-error-400";

/** Placeholder for a value that doesn't exist yet ("—", "..."). */
export const tableCellEmpty = "text-theme-sm text-gray-400 dark:text-gray-500";

/**
 * Cell padding per density. Header and body share the same scale so the header
 * label sits exactly above its column's content.
 */
export const tableCellPadding = cva("", {
	variants: {
		density: {
			comfortable: "px-6 py-4",
			compact: "px-4 py-3",
		},
	},
	defaultVariants: { density: "comfortable" },
});

/**
 * Action button variant styles
 */
export const actionButtonStyles: Record<string, string> = {
	default:
		"p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-white/[0.05]",
	success:
		"p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded transition-colors dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-green-950/20",
	danger:
		"p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-950/20",
	warning:
		"p-1.5 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors dark:text-gray-400 dark:hover:text-amber-400 dark:hover:bg-amber-950/20",
	info: "p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-950/20",
};

/**
 * Get action button styles
 */
export function getActionButtonStyles(variant: string = "default"): string {
	return actionButtonStyles[variant] || actionButtonStyles.default;
}
